import { InputState, DASState } from './state';
import { DAS_DELAY, DAS_RATE } from './constants';

const keyState: Record<string, boolean> = {};
const keyPressed: Record<string, boolean> = {}; // for single-press actions

export function initInput(): void {
  document.addEventListener('keydown', (e) => {
    if (!keyState[e.code]) {
      keyPressed[e.code] = true;
    }
    keyState[e.code] = true;

    // Prevent arrow keys from scrolling
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Space'].includes(e.code)) {
      e.preventDefault();
    }
  });

  document.addEventListener('keyup', (e) => {
    keyState[e.code] = false;
  });
}

// Consume a pressed key - returns true if it was pressed, then clears it
function consumePressed(key: string): boolean {
  if (keyPressed[key]) {
    keyPressed[key] = false;
    return true;
  }
  return false;
}

export function getInputState(): InputState {
  const input: InputState = {
    left: keyState['ArrowLeft'] || false,
    right: keyState['ArrowRight'] || false,
    rotateCW: consumePressed('ArrowUp') || consumePressed('KeyX'),
    rotateCCW: consumePressed('KeyZ') || consumePressed('ControlLeft') || consumePressed('ControlRight'),
    softDrop: keyState['ArrowDown'] || false,
    hardDrop: consumePressed('Space'),
    hold: consumePressed('KeyC') || consumePressed('ShiftLeft') || consumePressed('ShiftRight'),
  };

  return input;
}

export function updateDAS(das: DASState, input: InputState): { moveLeft: boolean; moveRight: boolean } {
  let moveLeft = false;
  let moveRight = false;

  // Determine current direction
  const newDirection: 'left' | 'right' | null =
    input.left && !input.right ? 'left' :
    input.right && !input.left ? 'right' : null;

  if (newDirection !== das.direction) {
    // Direction changed - reset DAS
    das.direction = newDirection;
    das.counter = 0;
    das.charged = false;

    // Immediate move on direction change
    if (newDirection === 'left') moveLeft = true;
    if (newDirection === 'right') moveRight = true;
  } else if (das.direction) {
    // Continue DAS
    das.counter++;

    if (!das.charged && das.counter >= DAS_DELAY) {
      das.charged = true;
      das.counter = 0;
    }

    if (das.charged && das.counter >= DAS_RATE) {
      das.counter = 0;
      if (das.direction === 'left') moveLeft = true;
      if (das.direction === 'right') moveRight = true;
    }
  }

  return { moveLeft, moveRight };
}

export function createDASState(): DASState {
  return {
    direction: null,
    counter: 0,
    charged: false,
  };
}

// For network mode - get raw key states without consuming pressed flags
export function getRawKeyState(): {
  left: boolean;
  right: boolean;
  rotateCW: boolean;
  rotateCCW: boolean;
  softDrop: boolean;
  hardDrop: boolean;
  hold: boolean;
  powerupSlot: number;
} {
  // Check for powerup keys (1-8)
  let powerupSlot = 0;
  for (let i = 1; i <= 8; i++) {
    if (consumePressed(`Digit${i}`)) {
      powerupSlot = i;
      break;
    }
  }

  const result = {
    left: keyState['ArrowLeft'] || false,
    right: keyState['ArrowRight'] || false,
    rotateCW: consumePressed('ArrowUp') || consumePressed('KeyX'),
    rotateCCW: consumePressed('KeyZ') || consumePressed('ControlLeft') || consumePressed('ControlRight'),
    softDrop: keyState['ArrowDown'] || false,
    hardDrop: consumePressed('Space'),
    hold: consumePressed('KeyC') || consumePressed('ShiftLeft') || consumePressed('ShiftRight'),
    powerupSlot,
  };

  return result;
}
